import { useEffect, useState } from "react";

const SECOND = 1000;

function pageVisible(): boolean {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

/**
 * The current time in milliseconds, refreshed every second while `enabled` and while the page
 * is visible. A hidden tab stops ticking (no wasted renders) and catches up when it returns.
 */
export function useNow(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return undefined;
    let timer: ReturnType<typeof setInterval> | undefined;
    const tick = () => {
      setNow(Date.now());
    };
    const start = () => {
      if (timer === undefined && pageVisible()) timer = setInterval(tick, SECOND);
    };
    const stop = () => {
      if (timer !== undefined) {
        clearInterval(timer);
        timer = undefined;
      }
    };
    const onVisibility = () => {
      if (pageVisible()) {
        tick();
        start();
      } else {
        stop();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled]);

  return now;
}
